import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import ProfilePictureFieldAdapter from 'components/shared/field-adapters/ProfilePictureFieldAdapter';
import StyledProfilePictureContainer from 'common/theme/profile/StyledProfilePictureContainer';
import ImageViewModal from 'components/shared/modals/ImageViewModal';

/**
 * Cabecera premium UNIFICADA para las pantallas de edición de usuarios
 * (admin / supervisor / cliente / entrenador), con la estética del perfil:
 * avatar grande centrado con aro dorado, nombre, chip de rol y email.
 * - `editable`: el avatar es un Field `photo` con dropzone + recorte (solo Admin).
 * - No editable: avatar de solo lectura; si hay foto, click la abre en grande.
 */
function UserEditHeader({
  editable = false,
  photo = null,
  firstName = '',
  lastName = '',
  email = '',
  roleLabel = null
}) {
  const [viewOpen, setViewOpen] = useState(false);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initials = `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();

  return (
    <Stack alignItems="center" spacing={1.25} sx={{ mb: 4 }}>
      {editable ? (
        <StyledProfilePictureContainer>
          <Field
            id="photo"
            name="photo"
            component={ProfilePictureFieldAdapter}
            firstName={firstName || ''}
            lastName={lastName || ''}
          />
        </StyledProfilePictureContainer>
      ) : (
        <Box
          onClick={() => photo && setViewOpen(true)}
          role={photo ? 'button' : undefined}
          aria-label={photo ? `Ver foto de ${fullName}` : undefined}
          sx={{
            borderRadius: '50%',
            p: '4px',
            border: '3px solid',
            borderColor: 'gold.main',
            cursor: photo ? 'pointer' : 'default',
            transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)',
            '&:hover': photo ? { transform: 'scale(1.03)' } : undefined,
          }}>
          <Avatar
            src={photo || undefined}
            alt={fullName}
            sx={{
              width: { xs: 110, md: 140 },
              height: { xs: 110, md: 140 },
              fontSize: '2.2rem',
              fontWeight: 800,
              backgroundColor: 'surface.raised',
              color: 'gold.main',
            }}>
            {initials}
          </Avatar>
        </Box>
      )}

      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '-0.01em', textAlign: 'center' }}>
        {fullName}
      </Typography>

      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" justifyContent="center">
        {roleLabel && (
          <Chip
            size="small"
            label={roleLabel}
            sx={{
              backgroundColor: 'rgba(249,189,78,0.14)',
              color: 'gold.main',
              fontWeight: 800,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
            }}
          />
        )}
        {email && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{email}</Typography>
        )}
      </Stack>

      <ImageViewModal
        open={viewOpen}
        src={photo}
        alt={fullName}
        onClose={() => setViewOpen(false)}
      />
    </Stack>
  );
}

UserEditHeader.propTypes = {
  editable: PropTypes.bool,
  photo: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  roleLabel: PropTypes.string,
};

export default UserEditHeader;
